/**
 * Keyboard Navigation Component
 * Provides comprehensive keyboard navigation support for animated interfaces
 */

import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useAccessibility } from '../lib/animations/useAccessibility';

interface KeyboardNavigationProps {
  children: ReactNode;
  enabled?: boolean;
  trapFocus?: boolean;
  restoreFocus?: boolean;
  skipLinks?: boolean;
  announceNavigation?: boolean;
}

interface FocusableElement extends HTMLElement {
  tabIndex: number;
}

const KeyboardNavigation: React.FC<KeyboardNavigationProps> = ({
  children,
  enabled = true,
  trapFocus = false,
  restoreFocus = true,
  skipLinks = true,
  announceNavigation = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [focusableElements, setFocusableElements] = useState<FocusableElement[]>([]);
  const [currentFocusIndex, setCurrentFocusIndex] = useState(-1);
  
  const { announce, focusElement } = useAccessibility({
    announceChanges: announceNavigation,
    enableKeyboardNavigation: enabled
  });

  // Get all focusable elements
  const getFocusableElements = (): FocusableElement[] => {
    if (!containerRef.current) return [];

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');

    const elements = Array.from(
      containerRef.current.querySelectorAll(focusableSelectors)
    ) as FocusableElement[];

    return elements.filter(el => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && 
             style.visibility !== 'hidden' && 
             !el.hasAttribute('aria-hidden');
    });
  };

  // Update focusable elements when children change
  useEffect(() => {
    if (!enabled) return;

    const updateFocusableElements = () => {
      const elements = getFocusableElements();
      setFocusableElements(elements);
    };

    updateFocusableElements();

    // Use MutationObserver to detect DOM changes
    const observer = new MutationObserver(updateFocusableElements);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['tabindex', 'disabled', 'aria-hidden']
      });
    }

    return () => observer.disconnect();
  }, [enabled, children]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const { key, shiftKey, ctrlKey, altKey } = event;

      // Skip if modifier keys are pressed (except Shift for Tab)
      if ((ctrlKey || altKey) && key !== 'Tab') return;

      switch (key) {
        case 'Tab':
          if (trapFocus && focusableElements.length > 0) {
            event.preventDefault();
            handleTabNavigation(shiftKey);
          }
          break;

        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          navigateToNext();
          break;

        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          navigateToPrevious();
          break;

        case 'Home':
          event.preventDefault();
          navigateToFirst();
          break;

        case 'End':
          event.preventDefault();
          navigateToLast();
          break;

        case 'Escape':
          if (trapFocus && restoreFocus && previousFocusRef.current) {
            event.preventDefault();
            focusElement(previousFocusRef.current);
            announce('Returned to previous focus');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, trapFocus, focusableElements, currentFocusIndex]);

  // Store previous focus when trap focus is enabled
  useEffect(() => {
    if (trapFocus && enabled) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // Focus first element when trap is enabled
      if (focusableElements.length > 0) {
        focusElement(focusableElements[0]);
        setCurrentFocusIndex(0);
      }
    }

    return () => {
      if (trapFocus && restoreFocus && previousFocusRef.current) {
        focusElement(previousFocusRef.current);
      }
    };
  }, [trapFocus, enabled, focusableElements]);

  const handleTabNavigation = (reverse: boolean) => {
    if (focusableElements.length === 0) return;

    let nextIndex;
    if (reverse) {
      nextIndex = currentFocusIndex <= 0 ? focusableElements.length - 1 : currentFocusIndex - 1;
    } else {
      nextIndex = currentFocusIndex >= focusableElements.length - 1 ? 0 : currentFocusIndex + 1;
    }

    focusElement(focusableElements[nextIndex]);
    setCurrentFocusIndex(nextIndex);
    
    if (announceNavigation) {
      const element = focusableElements[nextIndex];
      const label = element.getAttribute('aria-label') || 
                   element.textContent?.trim() || 
                   element.tagName.toLowerCase();
      announce(`Focused ${label}`);
    }
  };

  const navigateToNext = () => {
    if (focusableElements.length === 0) return;
    
    const nextIndex = (currentFocusIndex + 1) % focusableElements.length;
    focusElement(focusableElements[nextIndex]);
    setCurrentFocusIndex(nextIndex);
  };

  const navigateToPrevious = () => {
    if (focusableElements.length === 0) return;
    
    const prevIndex = currentFocusIndex <= 0 ? focusableElements.length - 1 : currentFocusIndex - 1;
    focusElement(focusableElements[prevIndex]);
    setCurrentFocusIndex(prevIndex);
  };

  const navigateToFirst = () => {
    if (focusableElements.length === 0) return;
    
    focusElement(focusableElements[0]);
    setCurrentFocusIndex(0);
    announce('Moved to first element');
  };

  const navigateToLast = () => {
    if (focusableElements.length === 0) return;
    
    const lastIndex = focusableElements.length - 1;
    focusElement(focusableElements[lastIndex]);
    setCurrentFocusIndex(lastIndex);
    announce('Moved to last element');
  };

  return (
    <div ref={containerRef} className="keyboard-navigation-container">
      {/* Skip links */}
      {skipLinks && (
        <div className="skip-links sr-only focus:not-sr-only">
          <motion.a
            href="#main-content"
            className="absolute top-0 left-0 z-50 bg-blue-600 text-white px-4 py-2 rounded-br-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            initial={{ y: -100 }}
            whileFocus={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            Skip to main content
          </motion.a>
          <motion.a
            href="#navigation"
            className="absolute top-0 left-32 z-50 bg-blue-600 text-white px-4 py-2 rounded-br-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            initial={{ y: -100 }}
            whileFocus={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            Skip to navigation
          </motion.a>
        </div>
      )}

      {/* Focus indicator */}
      {enabled && (
        <style jsx>{`
          .keyboard-navigation-container *:focus {
            outline: 2px solid #3B82F6;
            outline-offset: 2px;
            border-radius: 4px;
          }
          
          .keyboard-navigation-container *:focus:not(:focus-visible) {
            outline: none;
          }
          
          .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }
          
          .focus\\:not-sr-only:focus {
            position: static;
            width: auto;
            height: auto;
            padding: inherit;
            margin: inherit;
            overflow: visible;
            clip: auto;
            white-space: normal;
          }
        `}</style>
      )}

      {children}
    </div>
  );
};

export default KeyboardNavigation;